import MojitoCore from 'mojito/core';
import { createSlice } from '@reduxjs/toolkit';
import pdfService from './pdf-service.js';
import PdfUtils from './utils';

const reduxInstance = MojitoCore.Services.redux;

/**
 * Defines the state of the PDF store.
 *
 * @typedef PDFState
 *
 * @property {boolean} isFetching - Status for if a pdf is being fetched or not.
 * @property {boolean} error - Status for if an error has occurred when a trying to fetch a pdf.
 *
 *
 * @memberof Mojito.Services.PDF
 */

/**
 * The name of the PDF state property. Will be used to register in global redux store.
 * This state holds information about PDF details.
 *
 * @constant
 * @type {string}
 * @memberof Mojito.Services.PDF
 */
const STORE_KEY = 'PDFStore';

/**
 * PDF initial state.
 *
 * @typedef INITIAL_STATE
 * @memberof Mojito.Services.PDF
 */
const INITIAL_STATE = {
    isFetchingPdf: false,
    error: false,
};

const { reducer, actions } = createSlice({
    name: 'PDF',
    initialState: INITIAL_STATE,
    reducers: {
        downloadBetReceiptPending(state) {
            state.isFetchingPdf = true;
            state.error = false;
        },
        downloadBetReceiptSuccess(state) {
            state.isFetchingPdf = false;
            state.error = false;
        },
        downloadBetReceiptFailed(state) {
            state.isFetchingPdf = false;
            state.error = true;
        },
        reset() {
            return INITIAL_STATE;
        },
    },
});

/**
 * Download bet receipt thunk function.
 *
 * @function downloadBetReceipt
 *
 * @param {Array<Mojito.Services.Bets.types.Bet>} bets - The bets list.
 * @param {object} labels - The labels map object to be included in the receipt.
 * @param {string} filename - The filename for the downloaded receipt.
 *
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Thunk.
 *
 * @memberof Mojito.Services.PDF.actions
 */
actions.downloadBetReceipt = (bets, labels, filename) => {
    return dispatch => {
        dispatch(actions.downloadBetReceiptPending());
        pdfService
            .downloadBetReceipt(bets, labels)
            .then(res => {
                PdfUtils.writePdfToFile(res, filename);
                dispatch(actions.downloadBetReceiptSuccess());
            })
            .catch(() => dispatch(actions.downloadBetReceiptFailed()));
    };
};

/**
 * Set the status for if a pdf is being fetched or not.
 *
 * @function downloadBetReceiptPending
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {boolean} isFetchingPDF - Indicates pdf fetching status.
 *
 * @memberof Mojito.Services.PDF.actions
 */

/**
 * Set the status for if an error has occured when a trying to fetch a pdf.
 *
 * @function updateError
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {boolean} hasError - Indicates whether an error occurred during fetch.
 *
 * @memberof Mojito.Services.PDF.actions
 */

/**
 * Reset PDF state.
 *
 * @function reset
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @memberof Mojito.Services.PDF.actions
 */

reduxInstance.injectReducer(STORE_KEY, reducer);

export { reducer, actions, STORE_KEY, INITIAL_STATE };
